import { logger } from "@origin-digital/reporting-client";
import { fetchNativeFeatures } from "@origin-digital/event-dispatcher";
import {
  Backend,
  getCustomerId,
  getSessionCustomerType,
  getStoredCustomerType,
} from "./customerType";

export type MigrationContext = {
  storedCustomerType: Backend;
  sessionCustomerType: Backend;
};

export enum AppState {
  RX = "rx",
  R1 = "r1",
  UNKNOWN = "unknown",
}

const appStateToType = {
  [AppState.RX]: Backend.Kraken,
  [AppState.R1]: Backend.Sap,
};

export const isMismatchState = (
  context: MigrationContext,
  appState: AppState
): boolean => {
  if (
    context.storedCustomerType === Backend.Unknown ||
    context.sessionCustomerType === Backend.Unknown ||
    appState === AppState.UNKNOWN
  ) {
    return false;
  }

  // if all states do not match then it is considered a mismatch
  return !(
    context.storedCustomerType === context.sessionCustomerType &&
    appStateToType[appState] === context.storedCustomerType
  );
};

export const calculateAppState = (): AppState => {
  const r1Path = "/my-account/";
  const rxPath = "/my/";
  const path = window.location.pathname;

  if (path.startsWith(r1Path)) {
    return AppState.R1;
  }

  if (path.startsWith(rxPath)) {
    return AppState.RX;
  }

  return AppState.UNKNOWN;
};

export const getMigrationContext = async (): Promise<MigrationContext> => {
  const stored = await getStoredCustomerType();
  const session = await getSessionCustomerType();

  return {
    storedCustomerType: stored,
    sessionCustomerType: session,
  };
};

export const shouldShowMigratedModal = async (): Promise<boolean> => {
  const nativeFeatures = await fetchNativeFeatures({});
  const hasModalFeatures: boolean = !!(
    nativeFeatures?.features?.showModal && nativeFeatures?.features?.closeModal
  );

  if (!hasModalFeatures) {
    return false;
  }

  const context = await getMigrationContext();
  const appState = calculateAppState();
  const customerId = await getCustomerId();

  const shouldShowMigrationModal = isMismatchState(context, appState);
  const data = {
    ...context,
    appState,
    customerId,
    showModal: shouldShowMigrationModal,
  };

  logger.warn(`[migration-experience] Found app state.`, data);

  return shouldShowMigrationModal;
};
